import React, { useMemo, useState } from "react";
import { FileDown, Database, TrendingUp, Target } from "lucide-react";
import { useFetch } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Panel, StatusChip } from "@/components/app/primitives";
import { LiveBadge, fmtInt, fmtPct, fmtPt } from "@/components/app/livePrimitives";

/*
 * Live-engagement panels embedded in the existing demo modules.
 * Each panel renders ONLY in Live mode and is clearly labeled - live data and
 * curated demo content are never blended. All values are deterministic reads
 * of the live pipeline (files, calc runs, forensic view, metric catalog).
 */

const stateTone = (s) => ({ usable: "validated", mapped: "validated", profiled: "ai", partial: "risk", blocked: "danger", received: "neutral" }[s] || "neutral");

// ---------------- Evidence Room: live source register ----------------

export const LiveEvidencePanel = () => {
  const { mode } = useApp();
  const { data: files } = useFetch(mode === "live" ? "/live/files" : null);
  const { data: readiness } = useFetch(mode === "live" ? "/live/readiness" : null);
  if (mode !== "live") return null;
  const cards = readiness?.tables || [];
  const byId = Object.fromEntries(cards.map((c) => [c.file_id, c]));
  return (
    <Panel title="Live engagement evidence - uploaded Brightspeed sources" className="mb-4" testId="live-evidence-panel"
      right={<div className="flex items-center gap-2"><Database size={12} className="text-[--tx-3]" /><LiveBadge /></div>}>
      {(files || []).length === 0 && <p className="text-xs text-[--tx-3]">No live files yet - upload in the Data Intake Hub.</p>}
      <div className="grid md:grid-cols-2 gap-2">
        {(files || []).map((f) => {
          const r = byId[f.id] || {};
          return (
            <div key={f.id} className="rounded-xl border border-[--hairline] bg-[--shell-2] p-3" data-testid={`live-evidence-${f.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold text-[--tx-1] truncate">{f.filename}</span>
                <StatusChip tone={stateTone(f.review_state)} className="ml-auto shrink-0 capitalize">{f.review_state}</StatusChip>
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] font-mono text-[--tx-3]">
                <span>v{f.version} · {(f.upload_date || "").slice(0, 10)}</span>
                {f.profile_summary?.kind === "tabular" && <span>{fmtInt(f.profile_summary.total_rows)} rows</span>}
                {r.coverage?.[0] && <span>coverage {String(r.coverage[0]).slice(0, 10)} → {String(r.coverage[1]).slice(0, 10)}</span>}
                <span className={r.mapped ? "text-[--green]" : "text-[--amber]"}>{r.mapped ? "mapped" : "unmapped"}</span>
                {r.sensitive_columns?.length > 0 && <span className="text-[--six-crimson]">sensitive fields flagged</span>}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] font-mono text-[--tx-3] mt-2">Raw sources preserved verbatim server-side · mapping status and quality from the readiness engine · sensitive columns never surface in executive views</p>
    </Panel>
  );
};

// ---------------- Analytics Workspace: live driver cuts ----------------

export const LiveAnalyticsPanel = () => {
  const { mode } = useApp();
  const { data: run } = useFetch(mode === "live" ? "/live/calc/latest" : null);
  const [dim, setDim] = useState(null);
  const segments = useMemo(() => run?.segments || [], [run]);
  if (mode !== "live") return null;
  if (!run || run.status === "no_runs") return null;
  const dims = segments.map((s) => s.dimension);
  const active = segments.find((s) => s.dimension === (dim || (dims.includes("channel") ? "channel" : dims[0])));
  const focus = run.params?.focus || "focus month";
  const comparison = run.params?.comparison || "prior month";
  return (
    <Panel title={`Live data cuts - ${focus} vs ${comparison} (deterministic calc v${run.calc_version})`} className="mb-4" testId="live-analytics-panel"
      right={<div className="flex items-center gap-2"><TrendingUp size={12} className="text-[--tx-3]" /><LiveBadge /></div>}>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {dims.slice(0, 12).map((d) => (
          <button key={d} onClick={() => setDim(d)} data-testid={`live-cut-${d}`}
            className={`rounded-lg border px-2 py-1 text-[10px] font-mono capitalize transition-colors ${active?.dimension === d ? "border-[--ai] bg-[--ai-soft] text-[--ai]" : "border-[--hairline] bg-[--shell-3] text-[--tx-3] hover:text-[--tx-1]"}`}>
            {d.replace(/_/g, " ")}
          </button>
        ))}
      </div>
      <div className="space-y-1.5">
        {(active?.segments || []).slice(0, 8).map((s) => (
          <div key={s.segment} className="flex items-center gap-3 rounded-lg border border-[--hairline] bg-[--shell-2] px-3 py-1.5">
            <span className="text-xs font-medium text-[--tx-1] w-[30%] truncate">{s.segment}</span>
            <span className="text-[10px] font-mono text-[--tx-3]">rate {fmtPct(s.rate)}</span>
            <span className={`text-[11px] tabular-nums font-semibold ${(s.rate_delta || 0) > 0 ? "text-[--danger]" : "text-[--green]"}`}>{fmtPt(s.rate_delta)}</span>
            <span className="text-[11px] tabular-nums text-[--amber]">{s.volume_delta > 0 ? "+" : ""}{fmtInt(s.volume_delta)} cancels</span>
            <StatusChip tone={{ high: "validated", medium: "ai", low: "risk", insufficient: "danger" }[s.confidence] || "neutral"} className="ml-auto shrink-0 !text-[10px]">{s.confidence}</StatusChip>
          </div>
        ))}
      </div>
      <p className="text-[10px] font-mono text-[--tx-3] mt-2">Source: mapped Brightspeed workbooks · confidence reflects sample size · full forensic workspace under Live → August Forensic</p>
    </Panel>
  );
};

// ---------------- Pilot Board: cohort + proof-metric candidates ----------------

export const LivePilotCandidatesPanel = () => {
  const { mode } = useApp();
  const { data: cohorts } = useFetch(mode === "live" ? "/live/cohorts" : null);
  const { data: catalog } = useFetch(mode === "live" ? "/live/metrics/catalog" : null);
  if (mode !== "live") return null;
  const vintages = (cohorts?.available ? cohorts.vintages : []).filter((v) => v.vintage >= "2026-04").slice(-8);
  const proofMetrics = (catalog?.metrics || []).filter((m) => m.status === "computed" && typeof m.value === "number").slice(0, 6);
  if (vintages.length === 0 && proofMetrics.length === 0) return null;
  return (
    <Panel title="Live cohort & proof-metric candidates for pilot design" className="mb-4" testId="live-pilot-candidates"
      right={<div className="flex items-center gap-2"><Target size={12} className="text-[--tx-3]" /><LiveBadge /></div>}>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[--ai] mb-1.5">Measurable cohorts (from mapped order data)</div>
          <div className="space-y-1">
            {vintages.map((v) => (
              <div key={`${v.vintage}-${v.group}`} className="flex items-center gap-2 rounded-lg border border-[--hairline] bg-[--shell-2] px-2.5 py-1.5 text-[11px]">
                <span className="font-mono text-[--tx-2]">{v.vintage}</span>
                <span className="text-[--tx-1] font-medium">{v.group}</span>
                <span className="ml-auto font-mono text-[--tx-3]">{fmtInt(v.cancels)} cancels</span>
              </div>
            ))}
            {vintages.length === 0 && <p className="text-[11px] text-[--tx-3]">No order-level cohorts yet - ingest an order extract.</p>}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[--green] mb-1.5">Validated proof metrics (governed catalog)</div>
          <div className="space-y-1">
            {proofMetrics.map((m) => (
              <div key={m.key} className="flex items-center gap-2 rounded-lg border border-[--hairline] bg-[--shell-2] px-2.5 py-1.5 text-[11px]">
                <span className="text-[--tx-1] truncate">{m.label}</span>
                <span className="ml-auto font-mono tabular-nums text-[--tx-2] shrink-0">
                  {m.key.includes("rate") ? `${(m.value * 100).toFixed(2)}%` : m.value.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <p className="text-[10px] font-mono text-[--tx-3] mt-2">Pilot baselines and success thresholds can cite these cohorts and metrics directly - each carries formula, source, and version in the metric catalog</p>
    </Panel>
  );
};

// ---------------- Report Builder: facts / findings / hypotheses / gaps ----------------

export const LiveReportFactsPanel = () => {
  const { mode } = useApp();
  const { data: forensic } = useFetch(mode === "live" ? "/live/forensic" : null);
  const { data: dq } = useFetch(mode === "live" ? "/live/dq-log" : null);
  const facts = useMemo(() => {
    const out = [];
    const monthly = forensic?.monthly || [];
    const withData = monthly.filter((m) => m.pdf_cancels != null || m.xlsx_cancels != null);
    const last = withData[withData.length - 1];
    if (last) {
      const cancels = last.pdf_cancels ?? last.xlsx_cancels;
      const rate = last.pdf_rate ?? last.xlsx_rate;
      out.push(`${last.month}: ${Number(cancels).toLocaleString()} total cancels${rate != null ? ` at ${(rate * 100).toFixed(1)}% cancel rate` : ""} (computed from source files)`);
    }
    if (forensic?.reconciliation?.status) out.push(`Cross-source reconciliation: ${forensic.reconciliation.status}`);
    if (forensic?.cohort_analysis?.available) out.push(`${forensic.cohort_analysis.total_orders.toLocaleString()} canonical orders ingested (basis: ${forensic.cohort_analysis.basis?.replace(/_/g, " ")})`);
    return out;
  }, [forensic]);
  if (mode !== "live") return null;
  if (!forensic?.calc_run) return null;

  const findings = (forensic.hypotheses || []).filter((h) => ["supported", "directional"].includes(h.computed_status) || (h.evidence || []).some((e) => e.supports));
  const hypotheses = (forensic.hypotheses || []).filter((h) => !["supported", "disproved"].includes(h.computed_status));
  const gaps = (dq || []).filter((d) => d.category === "data_gap" && d.status !== "resolved");

  return (
    <Panel title="Live engagement report inputs - separated by epistemic status" className="mb-4" testId="live-report-facts"
      right={
        <div className="flex items-center gap-2">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/live/export/executive-packet.pdf`} data-testid="live-report-packet-link"
            className="inline-flex items-center gap-1 rounded-lg border border-[--hairline] bg-[--shell-3] px-2.5 py-1 text-[10px] font-semibold text-[--tx-2] hover:text-[--tx-1] transition-colors">
            <FileDown size={11} /> Executive packet (PDF)
          </a>
          <LiveBadge />
        </div>
      }>
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
        <div data-testid="live-report-facts-col">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[--green] mb-1.5">Known facts (computed)</div>
          {(facts.length ? facts : ["Calculation pass complete - see forensic workspace for computed monthly view"]).slice(0, 4).map((f, i) => (
            <p key={i} className="text-[11px] text-[--tx-2] leading-snug mb-1.5">• {f}</p>
          ))}
        </div>
        <div data-testid="live-report-findings-col">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[--ai] mb-1.5">Evidence-backed findings</div>
          {findings.slice(0, 4).map((h) => <p key={h.id} className="text-[11px] text-[--tx-2] leading-snug mb-1.5">• {h.title}</p>)}
          {findings.length === 0 && <p className="text-[11px] text-[--tx-3]">None yet - run agent analyses and Triad review.</p>}
        </div>
        <div data-testid="live-report-hypotheses-col">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[--amber] mb-1.5">Hypotheses to test</div>
          {hypotheses.slice(0, 4).map((h) => <p key={h.id} className="text-[11px] text-[--tx-2] leading-snug mb-1.5">• {h.title}</p>)}
          {hypotheses.length === 0 && <p className="text-[11px] text-[--tx-3]">No open hypotheses.</p>}
        </div>
        <div data-testid="live-report-gaps-col">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[--six-crimson] mb-1.5">Open questions / data gaps</div>
          {gaps.slice(0, 4).map((g) => <p key={g.id} className="text-[11px] text-[--tx-2] leading-snug mb-1.5">• {g.title}</p>)}
          {gaps.length === 0 && <p className="text-[11px] text-[--tx-3]">No open data gaps.</p>}
        </div>
      </div>
    </Panel>
  );
};
